import { useDispatch } from 'react-redux'
import { useMemo } from 'react'
import { IconName } from '@components/base'
import { useHasLargeScreen } from '@src/config/device'
import { DrawerParamList } from '@src/navigation/types'
import {
  ProfileScreen,
  InsightsScreen,
  NutritionistHubContainer,
  PlanScreen,
  NoraScreen,
} from '@screens'

export interface DrawerItemInfo {
  title: string
  routeName?: keyof DrawerParamList
  onItemPress?: () => void
  icon: IconName
  component?: React.ComponentType
  routeParams?: Record<string, any>
  tag?: string
  indicator?: string
  includeIf?: boolean
}

export const useDrawerItems = () => {
  const dispatch = useDispatch()
  const hasLargeScreen = useHasLargeScreen()

  const allDrawerItems = useMemo((): DrawerItemInfo[] => {
    return [
      {
        title: 'Home',
        routeName: 'Dashboard',
        icon: 'house-simple',
        includeIf: hasLargeScreen,
      },
      {
        title: 'Plan',
        routeName: 'Plan' as keyof DrawerParamList,
        icon: 'notepad',
        component: PlanScreen,
        includeIf: hasLargeScreen,
      },
      {
        title: 'Trends',
        routeName: 'Insights',
        icon: 'trend-up',
        component: InsightsScreen,
        includeIf: hasLargeScreen,
      },
      {
        title: 'Nutritionist',
        routeName: 'NutritionistHub',
        icon: 'users',
        component: NutritionistHubContainer,
        includeIf: hasLargeScreen,
      },
      {
        title: 'Nora',
        routeName: 'Nora',
        icon: 'sparkle',
        component: NoraScreen,
        includeIf: hasLargeScreen,
      },
      {
        title: 'Sign out',
        onItemPress: () => dispatch({ type: 'app/logout' }),
        icon: 'sign-out',
      },
    ]
  }, [dispatch, hasLargeScreen])

  return {
    visibleDrawerItems: allDrawerItems.filter(({ includeIf = true }) => includeIf),
    allDrawerItems,
  }
}

export interface DrawerRoute {
  routeName: keyof DrawerParamList
  component: React.ComponentType
}

export const useDrawerRoutes = (): DrawerRoute[] => {
  const { allDrawerItems } = useDrawerItems()
  const drawerRoutes = allDrawerItems
    .map(({ routeName, component }) => ({ routeName, component }))
    .filter(({ routeName, component }) => routeName && component) as DrawerRoute[]

  return [
    ...drawerRoutes,
    {
      routeName: 'Account',
      component: ProfileScreen,
    },
  ]
}
